import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import Select from "react-select";
import Button from "@mui/material/Button";
import MuiCard from "@mui/material/Card";
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useAuthService } from "@services/useAuthService";
import { useNavigate } from "react-router-dom";
import Toast from "./toast";
import { useTranslation } from "react-i18next";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import CircularProgress from "@mui/material/CircularProgress";
import { getSingleSelectStyles } from "@/lib/styles";
import { Link as RouterLink } from "react-router-dom";
import MuiLink from "@mui/material/Link";
import { Divider, Alert, IconButton } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SecurityIcon from "@mui/icons-material/Security";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SospitaLogo from "./CustomIcons";
import InputAdornment from "@mui/material/InputAdornment";
import useGetSupportedLanguages from "@/hooks/useGetSupportedLanguages";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(2.5),
  gap: theme.spacing(1.5),
  boxShadow: theme.shadows[8],
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  maxHeight: "calc(100vh - 48px)",
  overflowY: "auto",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(3),
  },
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.divider,
    borderRadius: "4px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1),
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
    },
    "&.Mui-focused": {
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
    },
  },
}));

const CompactAlert = styled(Alert)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  "& .MuiAlert-icon": {
    fontSize: "1.1rem",
  },
  "& .MuiAlert-message": {
    fontSize: "0.875rem",
  },
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "1rem",
  marginBottom: theme.spacing(1.5),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  "& svg": {
    fontSize: "1.1rem",
  },
}));

export default function SignUpCard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { signup, signin } = useAuthService();
  const theme = useTheme();
  const { toast, setToast, handleToastClose } = useApiErrorHandler();
  const singleSelectStyles = getSingleSelectStyles(theme);
  const { languageOptions, isLoading: languagesLoading } =
    useGetSupportedLanguages();

  // Get current language from localStorage or i18n current language
  const getCurrentLanguageOption = () => {
    const currentLang = localStorage.getItem("ln") || i18n.language || "en";
    if (languageOptions.length > 0) {
      return (
        languageOptions.find((o) => o.value === currentLang) ||
        languageOptions.find((o) => o.value === "en") ||
        languageOptions[0]
      );
    }
    return { value: "en", label: "🇺🇸 English", name: "English" };
  };

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [portalEl, setPortalEl] = useState(null);

  // Initialize form with current language from localStorage
  const getInitialLanguage = () => {
    const currentLang = localStorage.getItem("ln") || i18n.language || "en";
    // Return a basic option that will be updated when languageOptions load
    return {
      value: currentLang,
      label: `${currentLang.toUpperCase()}`,
      name: currentLang,
    };
  };

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting, isValid, touchedFields },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      language: getInitialLanguage(),
      orgId: "",
      orgname: "",
      username: "",
      firstname: "",
      lastname: "",
      email: "",
      password: "",
      confpassword: "",
    },
  });

  const selectedLanguage = watch("language");
  const password = watch("password");
  const watchedFields = watch();

  useEffect(() => {
    setPortalEl(document.body);

    // Ensure i18n language matches localStorage on mount
    const savedLang = localStorage.getItem("ln");
    if (savedLang && savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
  }, [i18n]);

  // Update form language when languageOptions are loaded
  useEffect(() => {
    if (languageOptions.length > 0) {
      const currentLang = localStorage.getItem("ln") || i18n.language || "en";
      const currentLangOption =
        languageOptions.find((o) => o.value === currentLang) ||
        languageOptions.find((o) => o.value === "en") ||
        languageOptions[0];
      setValue("language", currentLangOption);
      // Also set i18n language to match if different
      if (currentLangOption.value !== i18n.language) {
        i18n.changeLanguage(currentLangOption.value);
      }
    }
  }, [languageOptions, setValue, i18n]);

  useEffect(() => {
    if (selectedLanguage?.value) {
      localStorage.setItem("ln", selectedLanguage.value);
      i18n.changeLanguage(selectedLanguage.value);
    }
  }, [selectedLanguage, i18n]);

  const startIcon = (name, IconCmp) => (
    <InputAdornment position="start">
      <IconCmp
        sx={{ fontSize: "1rem" }}
        color={getFieldValidation(name) === "error" ? "error" : "action"}
      />
    </InputAdornment>
  );

  const onSubmit = async (data) => {
    try {
      const signupRes = await signup({
        acronym: data.orgId.trim(),
        organization_name: data.orgname.trim(),
        username: data.username.trim(),
        password: data.password,
        first_name: data.firstname.trim(),
        last_name: data.lastname.trim(),
        email: data.email.trim().toLowerCase(),
        language_tag:
          selectedLanguage?.value || localStorage.getItem("ln") || "en",
      });

      if (signupRes.status === 201) {
        setToast({
          open: true,
          severity: "success",
          message: t("signUpCard.successMessage"),
        });

        // Auto sign-in after successful signup
        try {
          await signin(
            {
              organization_acronym: data.orgId.trim(),
              username: data.username.trim(),
              password: data.password,
            },
            selectedLanguage?.value || localStorage.getItem("ln") || "en"
          );

          // After successful sign-in, navigate to home
          navigate("/");
        } catch (signInError) {
          console.error("Auto sign-in failed:", signInError);
          navigate("/sign-in");
        }
      }
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message:
          error.response?.data?.message ||
          t("signUpCard.errorMessage", "An error occurred during registration"),
      });
    }
  };

  const getFieldValidation = (fieldName) => {
    const hasError = errors[fieldName];
    const isTouched = touchedFields[fieldName];
    const hasValue = watchedFields[fieldName]?.length > 0;

    if (hasError) return "error";
    if (isTouched && hasValue) return "success";
    return "default";
  };

  return (
    <Card variant="outlined">
      {/* Compact Header Section */}
      <Box sx={{ textAlign: "center", mb: 1.5 }}>
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            justifyContent: "center",
            mb: 1.5,
          }}
        >
          <SospitaLogo fontSize="large" />
        </Box>
        <Typography
          component="h1"
          variant="h4"
          sx={{
            fontWeight: 600,
            fontSize: "clamp(1.5rem, 3vw, 1.875rem)",
            color: "text.primary",
            mb: 0.5,
          }}
        >
          {t("signUpCard.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("signUpCard.subtitle", "Create your enterprise safety account")}
        </Typography>

        {/* Compact Language Selection */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <Box sx={{ minWidth: 180 }}>
            <Controller
              name="language"
              control={control}
              rules={{ required: t("signUpCard.languageRequired") }}
              render={({ field }) => (
                <Select
                  {...field}
                  styles={{
                    ...singleSelectStyles,
                    control: (base, state) => ({
                      ...base,
                      minHeight: "32px",
                      fontSize: "13px",
                      borderColor: state.isFocused
                        ? theme.palette.primary.main
                        : theme.palette.divider,
                      boxShadow: state.isFocused
                        ? `0 0 0 2px ${theme.palette.primary.main}20`
                        : "none",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                  }}
                  options={
                    languageOptions.length > 0
                      ? languageOptions
                      : [getCurrentLanguageOption()]
                  }
                  isLoading={languagesLoading}
                  placeholder={t("signUpCard.languagePlaceholder")}
                  menuPortalTarget={portalEl}
                  menuPosition="fixed"
                  isSearchable={false}
                />
              )}
            />
            {errors.language && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.25, display: "block", fontSize: "0.7rem" }}
              >
                {errors.language.message}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Compact Security Notice */}
      <CompactAlert severity="info" icon={<SecurityIcon />} sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ fontSize: "0.8rem" }}>
          {t(
            "signUpCard.securityNotice",
            "All information is encrypted and stored securely"
          )}
        </Typography>
      </CompactAlert>

      {/* Registration Form */}
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {/* Organization Information */}
        <Box>
          <SectionHeader color="primary">
            <BusinessIcon />
            {t("signUpCard.organizationInfo", "Organization Information")}
          </SectionHeader>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              <StyledTextField
                label={t("signUpCard.orgIdLabel")}
                size="small"
                {...register("orgId", {
                  required: t("signUpCard.orgIdRequired"),
                  pattern: {
                    value: /^[A-Za-z0-9_-]+$/,
                    message: t(
                      "signUpCard.orgIdPattern",
                      "Only letters, numbers, hyphens and underscores allowed"
                    ),
                  },
                  minLength: {
                    value: 2,
                    message: t(
                      "signUpCard.orgIdMinLength",
                      "Organization ID must be at least 2 characters"
                    ),
                  },
                })}
                error={!!errors.orgId}
                helperText={errors.orgId?.message}
                slotProps={{
                  input: {
                    startAdornment: startIcon("orgId", BusinessIcon),
                    inputProps: {
                      "aria-label": t(
                        "signUpCard.orgIdAria",
                        "Organization ID input field"
                      ),
                      autoComplete: "organization",
                    },
                  },
                }}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <StyledTextField
                label={t("signUpCard.orgNameLabel")}
                size="small"
                {...register("orgname", {
                  required: t("signUpCard.orgNameRequired"),
                  minLength: {
                    value: 3,
                    message: t("signUpCard.orgNameMinLength"),
                  },
                })}
                error={!!errors.orgname}
                helperText={errors.orgname?.message}
                slotProps={{
                  input: {
                    startAdornment: startIcon("orgname", BusinessIcon),
                    inputProps: {
                      "aria-label": t(
                        "signUpCard.orgNameAria",
                        "Organization name input field"
                      ),
                    },
                  },
                }}
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>

        {/* Personal Information */}
        <Box>
          <SectionHeader color="primary">
            <AccountCircleIcon />
            {t("signUpCard.personalInfo", "Personal Information")}
          </SectionHeader>

          <StyledTextField
            label={t("signUpCard.usernameLabel")}
            autoComplete="username"
            size="small"
            {...register("username", {
              required: t("signUpCard.usernameRequired"),
              minLength: {
                value: 3,
                message: t("signUpCard.usernameMinLength"),
              },
              pattern: {
                value: /^[A-Za-z0-9._@-]+$/,
                message: t(
                  "signUpCard.usernamePattern",
                  "Invalid username format"
                ),
              },
            })}
            error={!!errors.username}
            helperText={errors.username?.message}
            slotProps={{
              input: {
                startAdornment: startIcon("username", PersonIcon),
                inputProps: {
                  "aria-label": t(
                    "signUpCard.usernameAria",
                    "Username input field"
                  ),
                  autoComplete: "username",
                },
              },
            }}
            fullWidth
            sx={{ mb: 1.5 }}
          />

          <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <StyledTextField
                label={t("signUpCard.firstNameLabel")}
                size="small"
                {...register("firstname", {
                  required: t("signUpCard.firstNameRequired"),
                  minLength: {
                    value: 2,
                    message: t("signUpCard.firstNameMinLength"),
                  },
                  pattern: {
                    value: /^[\p{L}\p{M}\s'-]+$/u,
                    message: t(
                      "signUpCard.firstNamePattern",
                      "Only letters, spaces, hyphens and apostrophes allowed"
                    ),
                  },
                })}
                error={!!errors.firstname}
                helperText={errors.firstname?.message}
                slotProps={{
                  input: {
                    startAdornment: startIcon("firstname", AccountCircleIcon),
                    inputProps: {
                      "aria-label": t(
                        "signUpCard.usernameAria",
                        "Username input field"
                      ),
                      autoComplete: "username",
                    },
                  },
                }}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <StyledTextField
                label={t("signUpCard.lastNameLabel")}
                size="small"
                {...register("lastname", {
                  required: t("signUpCard.lastNameRequired"),
                  minLength: {
                    value: 2,
                    message: t("signUpCard.lastNameMinLength"),
                  },
                  pattern: {
                    value: /^[\p{L}\p{M}\s'-]+$/u,
                    message: t(
                      "signUpCard.lastNamePattern",
                      "Only letters, spaces, hyphens and apostrophes allowed"
                    ),
                  },
                })}
                error={!!errors.lastname}
                helperText={errors.lastname?.message}
                slotProps={{
                  input: {
                    startAdornment: startIcon("lastname", AccountCircleIcon),
                    inputProps: {
                      "aria-label": t(
                        "signUpCard.usernameAria",
                        "Username input field"
                      ),
                      autoComplete: "username",
                    },
                  },
                }}
                fullWidth
              />
            </Grid>
          </Grid>

          <StyledTextField
            label={t("signUpCard.emailLabel")}
            type="email"
            autoComplete="email"
            size="small"
            {...register("email", {
              required: t("signUpCard.emailRequired"),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t("signUpCard.emailInvalid"),
              },
            })}
            error={!!errors.email}
            helperText={errors.email?.message}
            slotProps={{
              input: {
                startAdornment: startIcon("email", EmailIcon),
                inputProps: {
                  "aria-label": t("signUpCard.emailAria", "Email input field"),
                  autoComplete: "email",
                },
              },
            }}
            fullWidth
          />
        </Box>

        {/* Security Information */}
        <Box>
          <SectionHeader color="primary">
            <LockIcon />
            {t("signUpCard.securityInfo", "Security Information")}
          </SectionHeader>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <StyledTextField
                label={t("signUpCard.passwordLabel")}
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                size="small"
                {...register("password", {
                  required: t("signUpCard.passwordRequired"),
                  minLength: {
                    value: 8,
                    message: t("signUpCard.passwordMinLength"),
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                    message: t(
                      "signUpCard.passwordPattern",
                      "Password must contain uppercase, lowercase and number"
                    ),
                  },
                })}
                error={!!errors.password}
                helperText={errors.password?.message}
                slotProps={{
                  input: {
                    startAdornment: startIcon("password", LockIcon),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? (
                            <VisibilityOff sx={{ fontSize: "1rem" }} />
                          ) : (
                            <Visibility sx={{ fontSize: "1rem" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    inputProps: {
                      "aria-label": t(
                        "signUpCard.passwordAria",
                        "Password input field"
                      ),
                      autoComplete: "new-password",
                    },
                  },
                }}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <StyledTextField
                label={t("signUpCard.confirmPasswordLabel")}
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                size="small"
                {...register("confpassword", {
                  required: t("signUpCard.confirmPasswordRequired"),
                  validate: (value) =>
                    value === password || t("signUpCard.passwordsDoNotMatch"),
                })}
                error={!!errors.confpassword}
                helperText={errors.confpassword?.message}
                slotProps={{
                  input: {
                    startAdornment: startIcon("confpassword", LockIcon),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() => setShowConfirmPassword((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff sx={{ fontSize: "1rem" }} />
                          ) : (
                            <Visibility sx={{ fontSize: "1rem" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    inputProps: {
                      "aria-label": t(
                        "signUpCard.confirmPasswordAria",
                        "Confirm password input field"
                      ),
                      autoComplete: "new-password",
                    },
                  },
                }}
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>

        {/* Submit Button */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isSubmitting || !isValid}
          sx={{
            py: 1.25,
            fontSize: "0.95rem",
            fontWeight: 600,
            borderRadius: 2,
            textTransform: "none",
            boxShadow: theme.shadows[2],
            mt: 1,
            "&:hover": {
              boxShadow: theme.shadows[4],
            },
          }}
        >
          {isSubmitting ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} color="inherit" />
              <span>
                {t("signUpCard.creatingAccount", "Creating account...")}
              </span>
            </Box>
          ) : (
            t("signUpCard.submitButton")
          )}
        </Button>

        <Divider sx={{ my: 1.5 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.8rem" }}
          >
            {t("signUpCard.orDivider")}
          </Typography>
        </Divider>

        {/* Sign In Link */}
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.875rem" }}
          >
            {t("signUpCard.alreadyHaveAccount")}{" "}
            <MuiLink
              component={RouterLink}
              to="/sign-in"
              sx={{ fontWeight: 500, textDecoration: "none" }}
            >
              {t("signUpCard.signInLink")}
            </MuiLink>
          </Typography>
        </Box>
      </Box>

      <Toast
        open={toast.open}
        severity={toast.severity}
        message={toast.message}
        onClose={handleToastClose}
      />
    </Card>
  );
}
